import { useMemo, useState } from 'react';
import { Plus, Download, ArrowLeftRight, Info } from 'lucide-react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { TabBar } from '@/components/ui/TabBar';
import { LoadingState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { toCsv, downloadCsv } from '@/lib/csv';
import { Empty, money, moneyWhole } from '../outpatient/ui';
import type { PaymentMode } from '../types';
import { useBorrowIssues, useBorrowPayments, useBorrowReturns, useBorrowSettings, useBorrowSummary } from './useBorrow';
import { BorrowStatusBadges, SETTLEMENT_LABEL, dateOnly, expiryLabel, localDate, useDebounced } from './ui';
import IssueDialog from './IssueDialog';
import BorrowDetailDialog from './BorrowDetailDialog';
import type { BorrowFilters, BorrowIssue, BorrowPayment, BorrowReturn, BorrowStatusFilter, SettlementType } from './types';

/**
 * Medicine Borrow & Return — medicines lent to another hospital in an
 * emergency, and whether each loan has come back (same item) or been paid for.
 *
 * Kept apart from Sales & Returns on purpose: nothing here is a sale — no
 * invoice, no GST, no patient — and mixing the two would put a loan into the
 * till reconciliation. Stock movements still land in the one inventory (and
 * its per-batch History) as borrow_issue / borrow_return adjustments.
 */
type CsvRow = Record<string, unknown>;

type View = 'all' | 'pending-returns' | 'pending-payments' | 'overdue' | 'closed' | 'returned-items' | 'collections';

const VIEWS: Array<{ id: View; label: string }> = [
  { id: 'all', label: 'All records' },
  { id: 'pending-returns', label: 'Pending returns' },
  { id: 'pending-payments', label: 'Pending payments' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'closed', label: 'Closed' },
  { id: 'returned-items', label: 'Returned items' },
  { id: 'collections', label: 'Payment collections' },
];

/** The fixed filter each issue-list view implies; the "All" view lets the user pick. */
const VIEW_FILTERS: Partial<Record<View, BorrowFilters>> = {
  'pending-returns': { settlement_type: 'item_return', status: 'open' },
  'pending-payments': { settlement_type: 'payment', status: 'open' },
  overdue: { status: 'overdue' },
  closed: { status: 'closed' },
};

const STATUS_OPTIONS: Array<{ value: BorrowStatusFilter | ''; label: string }> = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open (not settled)' },
  { value: 'issued', label: 'Issued – pending' },
  { value: 'partially_returned', label: 'Partially returned' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'returned', label: 'Returned' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function BorrowTab() {
  const [view, setView] = useState<View>('all');
  const [hospital, setHospital] = useState('');
  const [item, setItem] = useState('');
  const [batch, setBatch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState<BorrowStatusFilter | ''>('');
  const [settlement, setSettlement] = useState<SettlementType | ''>('');
  const [mode, setMode] = useState<PaymentMode | ''>('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const text = {
    hospital: useDebounced(hospital.trim()),
    item: useDebounced(item.trim()),
    batch: useDebounced(batch.trim()),
  };
  const base: BorrowFilters = {
    hospital: text.hospital || undefined,
    item: text.item || undefined,
    batch: text.batch || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const isIssueView = view !== 'returned-items' && view !== 'collections';
  const issueFilters: BorrowFilters = view === 'all'
    ? { ...base, status: status || undefined, settlement_type: settlement || undefined }
    : { ...base, ...VIEW_FILTERS[view] };

  const summary = useBorrowSummary().data;
  // The hospital's policy (Admin Panel → Pharmacy → Medicine Borrow & Return). Off stops
  // new issues only — every open record below can still be settled.
  const settings = useBorrowSettings().data;
  const canIssue = !!settings?.enabled;
  const issuesQuery = useBorrowIssues(issueFilters, isIssueView);
  const returnsQuery = useBorrowReturns(base, view === 'returned-items');
  const paymentsQuery = useBorrowPayments({ ...base, mode: mode || undefined }, view === 'collections');

  const dateLabel = view === 'returned-items' ? 'Returned' : view === 'collections' ? 'Paid' : 'Issued';

  const exportCsv = () => {
    const stamp = localDate();
    if (view === 'returned-items') {
      const rows: CsvRow[] = (returnsQuery.data ?? []).map((r) => ({ ...r, returned_on: dateOnly(r.returned_on), expiry_date: expiryLabel(r.expiry_date) }));
      downloadCsv(`borrow-returns-${stamp}.csv`, toCsv<CsvRow>(rows, [
        ['returned_on', 'Returned on'], ['issue_number', 'Issue no.'], ['hospital_name', 'Hospital'], ['item_name', 'Item'],
        ['batch_number', 'Batch returned'], ['expiry_date', 'Expiry'], ['quantity', 'Quantity'], ['received_by', 'Received by'], ['notes', 'Notes'],
      ]));
    } else if (view === 'collections') {
      const rows: CsvRow[] = (paymentsQuery.data ?? []).map((p) => ({ ...p, paid_on: dateOnly(p.paid_on) }));
      downloadCsv(`borrow-payments-${stamp}.csv`, toCsv<CsvRow>(rows, [
        ['paid_on', 'Paid on'], ['issue_number', 'Issue no.'], ['hospital_name', 'Hospital'], ['item_name', 'Item'],
        ['amount', 'Amount'], ['payment_mode', 'Mode'], ['reference_number', 'Reference'], ['received_by', 'Received by'], ['notes', 'Notes'],
      ]));
    } else {
      const rows: CsvRow[] = (issuesQuery.data ?? []).map((i) => ({
        ...i,
        issue_date: dateOnly(i.issue_date),
        expected_return_date: dateOnly(i.expected_return_date),
        expiry_date: expiryLabel(i.expiry_date),
        settlement_type: SETTLEMENT_LABEL[i.settlement_type],
        status: `${i.status}${i.is_overdue ? ' (overdue)' : ''}`,
      }));
      downloadCsv(`borrow-records-${view}-${stamp}.csv`, toCsv<CsvRow>(rows, [
        ['issue_number', 'Issue no.'], ['issue_date', 'Issue date'], ['hospital_name', 'Hospital'], ['contact_person', 'Contact person'],
        ['contact_number', 'Contact number'], ['item_name', 'Item'], ['batch_number', 'Batch'], ['expiry_date', 'Expiry'],
        ['quantity', 'Quantity'], ['item_value', 'Value'], ['settlement_type', 'Settlement'], ['expected_return_date', 'Expected return'],
        ['returned_quantity', 'Returned qty'], ['paid_amount', 'Paid'], ['outstanding_quantity', 'Qty outstanding'],
        ['outstanding_amount', 'Amount outstanding'], ['status', 'Status'], ['reason', 'Reason'], ['issued_by', 'Issued by'],
      ]));
    }
  };

  const resetFilters = () => {
    setHospital(''); setItem(''); setBatch(''); setDateFrom(''); setDateTo(''); setStatus(''); setSettlement(''); setMode('');
  };

  return (
    <div className="space-y-4">
      {/* ── Headline figures ───────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Open records" value={summary ? String(summary.open_count) : '—'} hint={summary ? `${summary.closed_count} closed` : ''} accent="var(--teal)" />
        <Tile label="Items due back" value={summary ? `${summary.pending_return_units} units` : '—'} hint={summary ? `${summary.pending_return_count} record(s)` : ''} accent="var(--color-info)" />
        <Tile label="Amount outstanding" value={summary ? moneyWhole(summary.pending_payment_amount) : '—'} hint={summary ? `${summary.pending_payment_count} record(s) · ${moneyWhole(summary.collected_total)} collected` : ''} accent="var(--color-warn)" />
        <Tile label="Overdue" value={summary ? String(summary.overdue_count) : '—'} hint="past expected return date" accent="var(--color-danger)" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle><ArrowLeftRight size={15} /> Medicine Borrow &amp; Return</CardTitle>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={exportCsv}><Download size={13} /> CSV</Button>
            <Button size="sm" disabled={!canIssue} title={settings && !settings.enabled ? 'Switched off in Admin Panel → Pharmacy → Medicine Borrow & Return' : undefined} onClick={() => setCreating(true)}>
              <Plus size={13} /> New issue
            </Button>
          </div>
        </CardHeader>

        {settings && !settings.enabled && (
          <div className="mb-3 flex gap-2.5 rounded-lg bg-info-soft px-3 py-2.5 text-[12px] text-info-ink">
            <Info size={15} className="mt-px shrink-0" />
            <p>New issues are switched off by hospital policy (Admin Panel → Pharmacy → Medicine Borrow &amp; Return). Medicine already lent can still be returned or paid for.</p>
          </div>
        )}

        <TabBar tabs={VIEWS} active={view} onChange={setView} className="mb-3" />

        {/* ── Filters ─────────────────────────────────────────── */}
        <div className="mb-3 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <FormField label="Hospital"><Input value={hospital} onChange={(e) => setHospital(e.target.value)} /></FormField>
          <FormField label="Item"><Input value={item} onChange={(e) => setItem(e.target.value)} /></FormField>
          <FormField label="Batch"><Input value={batch} onChange={(e) => setBatch(e.target.value)} /></FormField>
          <FormField label={`${dateLabel} from`}><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></FormField>
          <FormField label={`${dateLabel} to`}><Input type="date" min={dateFrom || undefined} value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></FormField>
          {view === 'all' && (
            <FormField label="Status">
              <NativeSelect value={status} onChange={(e) => setStatus(e.target.value as BorrowStatusFilter | '')}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            </FormField>
          )}
          {view === 'all' && (
            <FormField label="Settlement type">
              <NativeSelect value={settlement} onChange={(e) => setSettlement(e.target.value as SettlementType | '')}>
                <option value="">Either</option>
                <option value="item_return">{SETTLEMENT_LABEL.item_return}</option>
                <option value="payment">{SETTLEMENT_LABEL.payment}</option>
              </NativeSelect>
            </FormField>
          )}
          {view === 'collections' && (
            <FormField label="Payment mode">
              <NativeSelect value={mode} onChange={(e) => setMode(e.target.value as PaymentMode | '')}>
                <option value="">Any</option>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
              </NativeSelect>
            </FormField>
          )}
          <div className="flex items-end">
            <Button variant="ghost" size="sm" onClick={resetFilters}>Clear filters</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isIssueView && <IssuesTable query={issuesQuery} onOpen={setOpenId} />}
          {view === 'returned-items' && <ReturnsTable query={returnsQuery} onOpen={setOpenId} />}
          {view === 'collections' && <PaymentsTable query={paymentsQuery} onOpen={setOpenId} />}
        </div>
      </Card>

      {creating && settings && (
        <IssueDialog
          settings={settings}
          onClose={() => setCreating(false)}
          onCreated={(issue) => { setCreating(false); setOpenId(issue.id); }}
        />
      )}
      {openId != null && <BorrowDetailDialog id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function Tile({ label, value, hint, accent }: { label: string; value: string; hint: string; accent: string }) {
  return (
    <StatCard accentSide="left" accentColor={accent}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-1 text-[22px] font-bold leading-none">{value}</div>
      <div className="mt-1 text-[11px] text-ink-muted">{hint}</div>
    </StatCard>
  );
}

interface ListQuery<T> { data?: T[]; isLoading: boolean }

function IssuesTable({ query, onOpen }: { query: ListQuery<BorrowIssue>; onOpen: (id: number) => void }) {
  const rows = useMemo(() => query.data ?? [], [query.data]);
  if (query.isLoading) return <LoadingState />;
  if (!rows.length) return <Empty label="No records match" hint="Change the filters, or record a new issue." />;
  return (
    <Table className="min-w-[980px]">
      <TableHeader>
        <TableRow>
          {['Issue no.', 'Issued', 'Hospital', 'Item', 'Batch / exp', 'Qty', 'Value', 'Settlement', 'Outstanding', 'Due', 'Status'].map((h) => <TableHead key={h}>{h}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((i) => (
          <TableRow key={i.id} className="cursor-pointer hover:bg-muted" onClick={() => onOpen(i.id)}>
            <TableCell className="font-mono text-[11.5px] font-semibold">{i.issue_number}</TableCell>
            <TableCell className="whitespace-nowrap text-[12px]">{dateOnly(i.issue_date)}</TableCell>
            <TableCell>
              <div className="font-semibold">{i.hospital_name}</div>
              {i.contact_person && <div className="text-[11px] text-ink-muted">{i.contact_person}{i.contact_number ? ` · ${i.contact_number}` : ''}</div>}
            </TableCell>
            <TableCell className="text-[12.5px]">{i.item_name}</TableCell>
            <TableCell className="whitespace-nowrap text-[11.5px]"><span className="font-mono">{i.batch_number}</span> · {expiryLabel(i.expiry_date)}</TableCell>
            <TableCell className="tabular-nums">{i.quantity}</TableCell>
            <TableCell className="tabular-nums">{money(i.item_value)}</TableCell>
            <TableCell className="text-[12px]">{SETTLEMENT_LABEL[i.settlement_type]}</TableCell>
            <TableCell className="whitespace-nowrap tabular-nums text-[12px]">
              {i.status === 'cancelled' ? '—'
                : i.settlement_type === 'item_return' ? `${i.outstanding_quantity} units` : money(i.outstanding_amount)}
            </TableCell>
            <TableCell className={`whitespace-nowrap text-[12px] ${i.is_overdue ? 'font-semibold text-danger' : ''}`}>{dateOnly(i.expected_return_date)}</TableCell>
            <TableCell><BorrowStatusBadges issue={i} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ReturnsTable({ query, onOpen }: { query: ListQuery<BorrowReturn>; onOpen: (id: number) => void }) {
  const rows = query.data ?? [];
  if (query.isLoading) return <LoadingState />;
  if (!rows.length) return <Empty label="No returns received for these filters" />;
  const units = rows.reduce((s, r) => s + r.quantity, 0);
  return (
    <>
      <Table className="min-w-[820px]">
        <TableHeader>
          <TableRow>
            {['Returned on', 'Issue no.', 'Hospital', 'Item', 'Batch returned', 'Qty', 'Received by'].map((h) => <TableHead key={h}>{h}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="cursor-pointer hover:bg-muted" onClick={() => onOpen(r.borrow_id)}>
              <TableCell className="whitespace-nowrap text-[12px]">{dateOnly(r.returned_on)}</TableCell>
              <TableCell className="font-mono text-[11.5px]">{r.issue_number}</TableCell>
              <TableCell>{r.hospital_name}</TableCell>
              <TableCell className="text-[12.5px]">{r.item_name}</TableCell>
              <TableCell className="whitespace-nowrap text-[11.5px]"><span className="font-mono">{r.batch_number}</span> · {expiryLabel(r.expiry_date)}</TableCell>
              <TableCell className="tabular-nums">+{r.quantity}</TableCell>
              <TableCell className="text-[12px] text-ink-muted">{r.received_by}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="mt-2 text-right text-[12px] text-ink-muted">{rows.length} return(s) · {units} units back in stock</div>
    </>
  );
}

function PaymentsTable({ query, onOpen }: { query: ListQuery<BorrowPayment>; onOpen: (id: number) => void }) {
  const rows = query.data ?? [];
  if (query.isLoading) return <LoadingState />;
  if (!rows.length) return <Empty label="No payments collected for these filters" />;
  const total = rows.reduce((s, p) => s + parseFloat(p.amount), 0);
  return (
    <>
      <Table className="min-w-[820px]">
        <TableHeader>
          <TableRow>
            {['Paid on', 'Issue no.', 'Hospital', 'Item', 'Mode', 'Reference', 'Amount', 'Received by'].map((h) => <TableHead key={h}>{h}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} className="cursor-pointer hover:bg-muted" onClick={() => onOpen(p.borrow_id)}>
              <TableCell className="whitespace-nowrap text-[12px]">{dateOnly(p.paid_on)}</TableCell>
              <TableCell className="font-mono text-[11.5px]">{p.issue_number}</TableCell>
              <TableCell>{p.hospital_name}</TableCell>
              <TableCell className="text-[12.5px]">{p.item_name}</TableCell>
              <TableCell className="text-[12px]">{p.payment_mode}</TableCell>
              <TableCell className="font-mono text-[11.5px]">{p.reference_number ?? '—'}</TableCell>
              <TableCell className="tabular-nums font-semibold">{money(p.amount)}</TableCell>
              <TableCell className="text-[12px] text-ink-muted">{p.received_by}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="mt-2 text-right text-[12px] text-ink-muted">{rows.length} payment(s) · {money(total)} collected</div>
    </>
  );
}
